import { axios, AxiosResponse } from "@sdk/axios";
import { SDKConfig } from "@sdk/default-sdk-config";
import { iFileRecord } from "@sdk/file-management/file-management.models";
import { EntityServices } from "@sdk/utils/entity.service";
import { UserTracker } from "user-tracker";
import { iHolidayQuoteX, iCreateQuoteWithItineraryRequest } from "./holiday-quotes-x-model";
import type {
  iHolidayQuoteConfirmBookingRequest,
  iHolidayQuoteConfirmBookingResponse,
} from "./holiday-quotes-x-model";
import { iOpportunity } from "../crm/crm.models";

export class HolidayQuotesXServices {
  configure(config: { basePath?: string; token?: string }) {
    Object.assign(this.config, config);
  }
  constructor(protected config: { basePath: string; token: string }) {
    this.holidayQuotesX.configure(config);
  }

  holidayQuotesX = Object.assign(
    new EntityServices<iHolidayQuoteX>(SDKConfig, "holiday-quotes-x", {
      onCreate: () => {
        UserTracker.track("HOLIDAY_QUOTES - Create", {});
      },
      onDelete: () => {
        UserTracker.track("HOLIDAY_QUOTES - Delete", {});
      },
    }),
    {
      sendQuote: async (quoteId: string) => {
        UserTracker.track("HOLIDAY_QUOTES - Send Quote");
        const results: AxiosResponse<any> = await axios.post(
          this.config.basePath + `/holiday-quotes-x/${quoteId}/send`,
          {},
          {
            headers: {
              Authorization: this.config.token,
            },
          }
        );
        return results.data;
      },
      quickAdd: async (req: {
        contactDetails: {
          brandId: string;
          firstName: string;
          lastName?: string;
          primaryEmail?: string;
          primaryMobile?: string;
        };
        opportunityDetails: Partial<iOpportunity>;
        holidayQuoteDetails: Partial<iHolidayQuoteX>;
      }) => {
        UserTracker.track("HOLIDAY_QUOTES - Quick Add");
        const results: AxiosResponse<iHolidayQuoteX> = await axios.post(
          this.config.basePath + `/holiday-quotes-x/quick-add`,
          req,
          {
            headers: {
              Authorization: this.config.token,
            },
          }
        );
        return results.data;
      },
      createQuoteWithItinerary: async (
        req: iCreateQuoteWithItineraryRequest
      ) => {
        UserTracker.track("HOLIDAY_QUOTES - Create Quote With Itinerary");
        const results: AxiosResponse<iHolidayQuoteX> = await axios.post(
          this.config.basePath + `/holiday-quotes-x/create-quote-with-itinerary`,
          req,
          {
            headers: {
              Authorization: this.config.token,
            },
          }
        );
        return results.data;
      },
      rateAndComment: async (
        quoteId: string,
        req: { ratings: number; comment: string }
      ) => {
        const results: AxiosResponse<any> = await axios.post(
          this.config.basePath +
            `/holiday-quotes-x/${quoteId}/rate-and-comment`,
          req,
          {
            headers: {
              Authorization: this.config.token,
            },
          }
        );
        return results.data;
      },
      generatePDF: async (quoteId: string) => {
        UserTracker.track("HOLIDAY_QUOTES - Generate PDF");
        const results: AxiosResponse<{ file: iFileRecord }> = await axios.post(
          this.config.basePath + `/holiday-quotes-x/${quoteId}/generate-pdf`,
          {},
          {
            headers: {
              Authorization: this.config.token,
            },
          }
        );
        return results.data;
      },
      generateImage: async (quoteId: string) => {
        UserTracker.track("HOLIDAY_QUOTES - Generate Image");
        const results: AxiosResponse<{ file: iFileRecord }> = await axios.post(
          this.config.basePath + `/holiday-quotes-x/${quoteId}/generate-image`,
          {},
          {
            headers: {
              Authorization: this.config.token,
            },
          }
        );
        return results.data;
      },
      addFeedback: async (quoteId: string, req: { comment: string }) => {
        const results: AxiosResponse<any> = await axios.post(
          this.config.basePath + `/holiday-quotes-x/${quoteId}/feedback`,
          req,
          {
            headers: {
              Authorization: this.config.token,
            },
          }
        );
        return results.data;
      },
      markFeedbackAsRead: async (
        quoteId: string,
        feedbackId: string,
        req: { status: boolean }
      ) => {
        const results: AxiosResponse<any> = await axios.post(
          this.config.basePath +
            `/holiday-quotes-x/${quoteId}/feedback/${feedbackId}/read`,
          req,
          {
            headers: {
              Authorization: this.config.token,
            },
          }
        );
        return results.data;
      },
      addViewLog: async (quoteId: string, req: { id: string }) => {
        const results: AxiosResponse<any> = await axios.post(
          this.config.basePath + `/holiday-quotes-x/${quoteId}/view-log`,
          req,
          {
            headers: {
              Authorization: this.config.token,
            },
          }
        );
        return results.data;
      },
      confirmBooking: async (
        quoteId: string,
        req: iHolidayQuoteConfirmBookingRequest
      ) => {
        const results: AxiosResponse<iHolidayQuoteConfirmBookingResponse> =
          await axios.post(
          this.config.basePath + `/holiday-quotes-x/${quoteId}/confirm-booking`,
          req,
          {
            headers: {
              Authorization: this.config.token,
            },
          }
        );
        return results.data;
      },
    }
  );
}
